/**
 * Pick'ems: weeks, submissions, scoring and standings.
 *
 * Extracted from the 4,132-line `services/supabaseDataManager.js` god class.
 * Every function takes the shared `ctx` ({ client, seasonsCache, activeSeasonId })
 * as its first argument; see `./context.js`.
 */

import * as models from '../../types/index.js';
import { formatFromDatabase } from './caseMap.js';
import { throwDbError } from './errors.js';
import { createLogger } from './logger.js';
import { getSeason } from './seasons.js';
import { getUserDisplayNames } from './users.js';
import { isDecided } from '../../src/components/pickems/pickEmStandings.js';

const log = createLogger('db:pickems');
// Pick'em week management
export async function createPickEmWeek(ctx, seasonId, weekNumber, customSchedule = null) {

  try {
    // Nothing is sent unless a caller chose a time. The database derives the
    // window, and only it can see the NFL calendar: a deadline worked out here
    // would be passed in as a choice and would override the first-kickoff rule.
    const { data, error } = await ctx.client.rpc('create_pick_em_week', {
      p_season_id: seasonId,
      p_week_number: weekNumber,
      p_submission_opens_at: customSchedule?.submissionOpensAt ?? null,
      p_submission_closes_at: customSchedule?.submissionClosesAt ?? null,
      p_results_reveal_at: customSchedule?.resultsRevealAt ?? null
    });

    if (error) throw error;

    return data;
  } catch (error) {
    throwDbError(error, 'Create pick em week');
  }
}

/**
 * Make sure a week has its pick'em row, creating it if not.
 *
 * The weekly sync's `pickEmWeek` step. Until 2026-09-03 the row was created
 * by an admin pressing a button in the Pick'ems tab every Tuesday, and a week
 * with no row has no pick'em form and no TD parlay — so a missed morning was
 * a missed week for the whole league.
 *
 * **It is idempotent**, which is what separates it from `createPickEmWeek`
 * above. `(season_id, week_number)` is unique, so a second call would raise;
 * checking first makes a re-run, a Wednesday roster refresh, or an admin who
 * already pressed the button all a quiet no-op.
 *
 * @returns {Promise<{ created: boolean, id: string }>}
 */
export async function ensurePickEmWeek(ctx, seasonId, weekNumber) {
  try {
    const existing = await getPickEmWeek(ctx, seasonId, weekNumber);
    if (existing) return { created: false, id: existing.id };

    const { data, error } = await ctx.client.rpc('create_pick_em_week', {
      p_season_id: seasonId,
      p_week_number: weekNumber
    });

    if (error) throw error;

    log.info(`week ${weekNumber}: pick'em week created`);

    return { created: true, id: data };
  } catch (error) {
    throwDbError(error, 'Ensure pick em week');
  }
}

export async function getPickEmWeek(ctx, seasonId, weekNumber) {

  try {
    // `maybeSingle`, not `single`: a week with no pick'em row is normal (the
    // playoff weeks have none), and `single` made PostgREST answer 406 with
    // PGRST116. The code already treated that as "no row", but the failed
    // request still showed up as a console error on every load.
    const { data, error } = await ctx.client
      .from('pick_em_weeks')
      .select('*')
      .eq('season_id', seasonId)
      .eq('week_number', weekNumber)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') throw error;

    return data ? formatFromDatabase(data) : null;
  } catch (error) {
    throwDbError(error, 'Get pick em week');
  }
}

export async function getAllPickEmWeeks(ctx, seasonId) {

  try {
    const { data, error } = await ctx.client
      .from('pick_em_weeks')
      .select('*')
      .eq('season_id', seasonId)
      .order('week_number');

    if (error) throw error;

    return (data || []).map(formatFromDatabase);
  } catch (error) {
    throwDbError(error, 'Get all pick em weeks');
  }
}

export async function getPickEmStatus(ctx, seasonId) {

  try {
    // Get all pick'em weeks for the season
    const { data: pickEmWeeks, error: weeksError } = await ctx.client
      .from('pick_em_weeks')
      .select('*')
      .eq('season_id', seasonId)
      .order('week_number');

    if (weeksError) throw weeksError;

    if (!pickEmWeeks || pickEmWeeks.length === 0) {
      return [];
    }

    const now = new Date();
    const statusResults = [];

    for (const week of pickEmWeeks) {
      const submissionOpensAt = new Date(week.submission_opens_at);
      const submissionClosesAt = new Date(week.submission_closes_at);
      const resultsRevealAt = new Date(week.results_reveal_at);

      // Check if all games for this week are completed
      const { data: games, error: gamesError } = await ctx.client
        .from('games')
        .select('is_completed')
        .eq('season_id', seasonId)
        .eq('week', week.week_number);

      if (gamesError) throw gamesError;

      const allGamesCompleted = games && games.length > 0 && games.every(g => g.is_completed);

      // Determine status
      let status = 'upcoming';
      let canSubmit = false;
      let resultsAvailable = false;
      let timeInfo = '';

      if (now < submissionOpensAt) {
        status = 'upcoming';
        timeInfo = `Opens ${submissionOpensAt.toLocaleDateString()}`;
      } else if (now >= submissionOpensAt && now < submissionClosesAt) {
        status = 'open';
        canSubmit = true;
        timeInfo = `Closes ${submissionClosesAt.toLocaleDateString()}`;
      } else if (now >= submissionClosesAt) {
        status = 'closed';

        // Results are available if all games are completed OR we're past reveal time
        if (allGamesCompleted || now >= resultsRevealAt) {
          resultsAvailable = true;
          status = 'completed';
          timeInfo = 'Results Available';
        } else {
          timeInfo = `Results reveal ${resultsRevealAt.toLocaleDateString()}`;
        }
      }

      statusResults.push({
        weekNumber: week.week_number,
        pickEmWeekId: week.id,
        status,
        canSubmit,
        resultsAvailable,
        timeInfo,
        allGamesCompleted,
        submissionOpensAt: week.submission_opens_at,
        submissionClosesAt: week.submission_closes_at,
        resultsRevealAt: week.results_reveal_at
      });
    }

    return statusResults;
  } catch (error) {
    throwDbError(error, 'Get pick em status');
    return [];
  }
}

// Pick'em submissions
export async function submitPickEmPicks(ctx, pickEmWeekId, picks) {

  if (!Array.isArray(picks) || picks.length === 0) {
    throw new Error('Picks must be a non-empty array');
  }

  // Validate all picks
  for (const pick of picks) {
    if (!models.validatePickEmSubmission({
      pickEmWeekId,
      gameId: pick.gameId,
      predictedWinnerTeamId: pick.predictedWinnerTeamId
    })) {
      throw new Error('Invalid pick submission data');
    }
  }

  try {
    const { data, error } = await ctx.client.rpc('submit_pick_em_picks', {
      p_pick_em_week_id: pickEmWeekId,
      p_picks: picks
    });

    if (error) throw error;

    return data || [];
  } catch (error) {
    throwDbError(error, 'Submit pick em picks');
  }
}

export async function getUserPicksForWeek(ctx, pickEmWeekId, userId = null) {

  try {

    // Get current user session
    const { data: { session } } = await ctx.client.auth.getSession();
    const currentUserId = session?.user?.id;

    // Use current user ID if none provided
    const targetUserId = userId || currentUserId;

    const { data, error } = await ctx.client.rpc('get_user_picks_for_week', {
      p_pick_em_week_id: pickEmWeekId,
      p_user_id: targetUserId
    });

    if (error) throw error;


    // Transform snake_case to camelCase for frontend
    const transformedData = (data || []).map(pick => ({
      submissionId: pick.submission_id,
      gameId: pick.game_id,
      weekNumber: pick.week_number,
      team1Name: pick.team1_name,
      team2Name: pick.team2_name,
      predictedWinnerTeamId: pick.predicted_winner_team_id,
      predictedWinnerName: pick.predicted_winner_name,
      confidenceLevel: pick.confidence_level,
      isCorrect: pick.is_correct,
      pointsEarned: pick.points_earned,
      actualWinnerTeamId: pick.actual_winner_team_id,
      actualWinnerName: pick.actual_winner_name,
      submittedAt: pick.submitted_at
    }));


    return transformedData;
  } catch (error) {
    throwDbError(error, 'Get user picks for week');
    return [];
  }
}

/**
 * Did this pick call the game? Decided on the fly from the game's own result. A
 * tie has no winner and is correct for nobody, and a row with no stored winner
 * must not match it.
 */
const isPickCorrect = (game, submission) =>
  Boolean(game?.is_completed) &&
  submission.predicted_winner_team_id != null &&
  game.winner_team_id === submission.predicted_winner_team_id;

export async function getAllPicksForWeek(ctx, pickEmWeekId) {

  try {
    // Get all submissions with game and team data
    const { data, error } = await ctx.client
      .from('pick_em_submissions')
      .select(`
        *,
        pick_em_weeks!inner(is_completed),
        games(
          week,
          is_completed,
          winner_team_id,
          team1_score,
          team2_score,
          team1:teams!games_team1_id_fkey(id, name, owner, franchise_id),
          team2:teams!games_team2_id_fkey(id, name, owner, franchise_id)
        ),
        predicted_team:teams!pick_em_submissions_predicted_winner_team_id_fkey(name)
      `)
      .eq('pick_em_week_id', pickEmWeekId);

    if (error) throw error;

    // Get unique user IDs
    const userIds = [...new Set((data || []).map(s => s.user_id))];
    const displayNames = await getUserDisplayNames(ctx, userIds);

    // Calculate results on the fly by comparing picks to actual game results
    const submissions = (data || []).map(submission => {
      const game = submission.games;
      const isCorrect = isPickCorrect(game, submission);
      const pointsEarned = isCorrect ? 1 : 0; // Simple scoring: 1 point per correct pick
      const formattedSubmission = formatFromDatabase(submission);

      return {
        ...formattedSubmission,
        displayName: displayNames[submission.user_id] || `User ${submission.user_id?.slice(0, 8)}`,
        isCorrect,
        pointsEarned,
        pickedTeamId: submission.predicted_winner_team_id,
        pickedTeamName: formattedSubmission.predictedTeam?.name,
        predictedWinnerName: formattedSubmission.predictedTeam?.name,
        actualWinnerTeamId: game?.winner_team_id,
        actualWinnerName: game?.winner_team_id === game?.team1?.id ? game?.team1?.name :
          game?.winner_team_id === game?.team2?.id ? game?.team2?.name : null,
        team1Id: game?.team1?.id,
        team2Id: game?.team2?.id,
        team1Name: game?.team1?.name,
        team2Name: game?.team2?.name,
        team1Score: game?.team1_score,
        team2Score: game?.team2_score,
        gameCompleted: game?.is_completed
      };
    });

    return submissions;
  } catch (error) {
    throwDbError(error, 'Get all picks for week');
  }
}

export async function getAdminSubmissionsForWeek(ctx, pickEmWeekId) {

  try {
    const { data, error } = await ctx.client
      .from('pick_em_submissions')
      .select(`
        *,
        games(
          week,
          is_completed,
          winner_team_id,
          team1:teams!games_team1_id_fkey(id, name, owner, franchise_id),
          team2:teams!games_team2_id_fkey(id, name, owner, franchise_id)
        ),
        predicted_team:teams!pick_em_submissions_predicted_winner_team_id_fkey(name)
      `)
      .eq('pick_em_week_id', pickEmWeekId)
      .order('submitted_at', { ascending: false });

    if (error) throw error;

    const submissions = (data || []).map((submission) => ({
      ...formatFromDatabase(submission),
      isCorrect: isPickCorrect(submission.games, submission),
      gameCompleted: Boolean(submission.games?.is_completed)
    }));

    // Get user details for each unique user ID
    const userIds = [...new Set(submissions.map(s => s.userId))];
    const userDetails = {};

    // Try to get user details using the RPC function first
    try {
      const { data: usersData, error: usersError } = await ctx.client.rpc('get_users_for_admin', {
        user_ids: userIds
      });

      if (!usersError && usersData && Array.isArray(usersData)) {
        usersData.forEach(user => {
          if (user && user.id) {
            userDetails[user.id] = {
              email: user.email || `user-${user.id.slice(0, 8)}@unknown.com`,
              displayName: user.display_name || user.email || `User ${user.id.slice(0, 8)}`
            };
          }
        });
      } else {
        log.warn('RPC function get_users_for_admin failed or returned no data:', usersError);
      }
    } catch (rpcError) {
      log.warn('RPC function get_users_for_admin not available. Please run the database migration in /database/admin_user_details_migration.sql');
      log.warn('Error details:', rpcError);
    }

    // Fallback: get current user details for comparison
    try {
      const { data: currentUserData } = await ctx.client.auth.getUser();
      if (currentUserData?.user?.id) {
        userDetails[currentUserData.user.id] = {
          email: currentUserData.user.email,
          displayName: currentUserData.user.user_metadata?.name || currentUserData.user.email
        };
      }
    } catch (authError) {
      log.warn('Could not get current user details:', authError);
    }

    // Apply user details to submissions with fallbacks
    return submissions.map(submission => ({
      ...submission,
      userDetails: userDetails[submission.userId] || {
        email: `user-${submission.userId?.slice(0, 8) || 'unknown'}@needs-migration.com`,
        displayName: `User ${submission.userId?.slice(0, 8) || 'Unknown'} (Run DB Migration)`
      }
    }));
  } catch (error) {
    throwDbError(error, 'Get admin submissions for week');
    return [];
  }
}

// Pick'em results and scoring
export async function calculatePickEmResults(ctx, pickEmWeekId) {

  try {
    // Since we calculate results on the fly, we just need to mark the week as completed
    // and ensure all games for the week are completed

    // First, verify all games are completed
    const { data: pickEmWeek, error: weekError } = await ctx.client
      .from('pick_em_weeks')
      .select('week_number, season_id')
      .eq('id', pickEmWeekId)
      .single();

    if (weekError) throw weekError;

    const { data: games, error: gamesError } = await ctx.client
      .from('games')
      .select('is_completed')
      .eq('season_id', pickEmWeek.season_id)
      .eq('week', pickEmWeek.week_number);

    if (gamesError) throw gamesError;

    const allGamesCompleted = games && games.length > 0 && games.every(g => g.is_completed);

    if (!allGamesCompleted) {
      throw new Error('Cannot calculate results: Not all games for this week are completed');
    }

    // Mark the week as completed
    const { error: updateError } = await ctx.client
      .from('pick_em_weeks')
      .update({
        is_completed: true,
        is_closed: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', pickEmWeekId);

    if (updateError) throw updateError;

    // Return success - results are calculated on the fly when requested
    return { success: true, message: 'Results calculated successfully' };
  } catch (error) {
    throwDbError(error, 'Calculate pick em results');
  }
}

export async function getWeeklyPickEmScores(ctx, pickEmWeekId) {

  try {
    // Get all picks for the week with calculated results
    const allPicks = await getAllPicksForWeek(ctx, pickEmWeekId);

    if (!allPicks || allPicks.length === 0) {
      return [];
    }

    // Group picks by user
    const userScores = {};
    allPicks.forEach(pick => {
      const userId = pick.userId;
      if (!userScores[userId]) {
        userScores[userId] = {
          userId,
          totalPicks: 0,
          decidedPicks: 0,
          correctPicks: 0,
          totalPoints: 0,
          pickEmWeekId
        };
      }

      userScores[userId].totalPicks++;
      if (isDecided(pick)) userScores[userId].decidedPicks++;
      if (pick.isCorrect) {
        userScores[userId].correctPicks++;
        userScores[userId].totalPoints += pick.pointsEarned || 1;
      }
    });

    // Get display names for all users
    const userIds = Object.keys(userScores);
    const displayNames = await getUserDisplayNames(ctx, userIds);

    // Convert to array and calculate accuracy and rank
    const scoresArray = Object.values(userScores).map(score => ({
      ...score,
      displayName: displayNames[score.userId] || `User ${score.userId.slice(0, 8)}`,
      // `isComplete` is what separates "7 from 7" from "1 from 1 so far", and
      // both read as 100%. Anything that means *perfect* has to check it — see
      // `perfectWeeks` below, and the Results tab's own tile.
      isComplete: score.totalPicks > 0 && score.decidedPicks === score.totalPicks,
      accuracyPercentage:
        score.decidedPicks > 0 ? (score.correctPicks / score.decidedPicks) * 100 : 0
    }));

    // Sort by total points (desc), then by correct picks (desc), then by accuracy (desc)
    scoresArray.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
      if (b.correctPicks !== a.correctPicks) return b.correctPicks - a.correctPicks;
      return b.accuracyPercentage - a.accuracyPercentage;
    });

    // Assign ranks
    return scoresArray.map((score, index) => ({
      ...score,
      weeklyRank: index + 1
    }));
  } catch (error) {
    throwDbError(error, 'Get weekly pick em scores');
    return [];
  }
}

// Get all picks from all weeks in a season. Each row carries its `weekNumber`,
// which is what the Standings tab's tourneys filter on.
export async function getAllSeasonPicks(ctx, seasonId) {

  try {
    // Get all pick'em weeks for the season
    const { data: pickEmWeeks, error: weeksError } = await ctx.client
      .from('pick_em_weeks')
      .select('id, week_number')
      .eq('season_id', seasonId)
      .order('week_number');

    if (weeksError) throw weeksError;

    if (!pickEmWeeks || pickEmWeeks.length === 0) {
      return [];
    }

    // Get all picks for all weeks
    const allSeasonPicks = [];

    for (const week of pickEmWeeks) {
      const weekPicks = await getAllPicksForWeek(ctx, week.id);
      allSeasonPicks.push(...weekPicks.map((pick) => ({ ...pick, weekNumber: week.week_number })));
    }

    return allSeasonPicks;
  } catch (error) {
    throwDbError(error, 'Get all season picks');
    return [];
  }
}

// Pick'em analytics
export async function getPickEmWeeklyBreakdown(ctx, seasonId) {

  try {
    // Get all pick'em weeks for the season
    const { data: pickEmWeeks, error } = await ctx.client
      .from('pick_em_weeks')
      .select('id, week_number')
      .eq('season_id', seasonId)
      .order('week_number');

    if (error) throw error;

    // Get scores for each week and group by week number
    const weeklyBreakdown = {};

    for (const week of pickEmWeeks || []) {
      const scores = await getWeeklyPickEmScores(ctx, week.id);
      weeklyBreakdown[week.week_number] = scores;
    }

    return weeklyBreakdown;
  } catch (error) {
    throwDbError(error, 'Get pick em weekly breakdown');
    return {};
  }
}

export async function getPickEmGameData(ctx, seasonId, weekNumber) {

  try {
    // Get games for the week to use for pick'ems. `franchise_id` rides along
    // because a team's colour and avatar are keyed on it (`teamColors.js`);
    // without it the form's teams fell back to an owner-keyed hue that did not
    // match the same franchise on Schedule.
    const { data, error } = await ctx.client
      .from('games')
      .select(`
        id,
        week,
        team1_id,
        team2_id,
        team1_score,
        team2_score,
        is_completed,
        winner_team_id,
        team1:teams!games_team1_id_fkey(id, name, owner, franchise_id, wins, losses, ties),
        team2:teams!games_team2_id_fkey(id, name, owner, franchise_id, wins, losses, ties)
      `)
      .eq('season_id', seasonId)
      .eq('week', weekNumber)
      .order('id');

    if (error) throw error;

    return (data || []).map(game => ({
      ...formatFromDatabase(game),
      canPredict: !game.is_completed // Can only predict on incomplete games
    }));
  } catch (error) {
    throwDbError(error, 'Get pick em game data');
  }
}

// Administrative functions
export async function createPickEmWeeksForSeason(ctx, seasonId, startWeek = 1, endWeek = null) {

  try {
    const season = await getSeason(ctx, seasonId);
    if (!season) throw new Error('Season not found');

    const finalWeek = endWeek || season.regularSeasonWeeks;
    const createdWeeks = [];

    for (let week = startWeek; week <= finalWeek; week++) {
      try {
        const pickEmWeekId = await createPickEmWeek(ctx, seasonId, week);
        createdWeeks.push({ week, pickEmWeekId });
      } catch (error) {
        // Skipping is intentional — re-running this for a season that already
        // has some weeks should fill the gaps, not abort on the first
        // duplicate. The returned list says which weeks were actually created.
        log.warn(`week ${week} not created:`, error?.message ?? error);
      }
    }

    return createdWeeks;
  } catch (error) {
    throwDbError(error, 'Create pick em weeks for season');
  }
}

export async function updatePickEmWeekStatus(ctx, pickEmWeekId, status) {

  const statusUpdates = {};

  switch (status) {
    case models.PICK_EM_STATUS.OPEN:
      statusUpdates.is_active = true;
      statusUpdates.is_closed = false;
      break;
    case models.PICK_EM_STATUS.CLOSED:
      statusUpdates.is_active = false;
      statusUpdates.is_closed = true;
      break;
    case models.PICK_EM_STATUS.COMPLETED:
      statusUpdates.is_active = false;
      statusUpdates.is_closed = true;
      statusUpdates.is_completed = true;
      break;
    default:
      statusUpdates.is_active = false;
      statusUpdates.is_closed = false;
      statusUpdates.is_completed = false;
  }

  try {
    const { data, error } = await ctx.client
      .from('pick_em_weeks')
      .update(statusUpdates)
      .eq('id', pickEmWeekId)
      .select()
      .single();

    if (error) throw error;

    return formatFromDatabase(data);
  } catch (error) {
    throwDbError(error, 'Update pick em week status');
  }
}
